"use client";

import {
  ArrowRight,
  ClipboardList,
  MessageSquareText,
  PackageCheck,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  UsersRound,
} from "lucide-react";
import { motion } from "motion/react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/ui/button";
import { Link } from "@/i18n/navigation";

import {
  AGENTIC_OPERATION_KEYS,
  type AgenticOperationKey,
} from "./agenticOperationsData";

const areaPresentation: Record<
  AgenticOperationKey,
  {
    icon: typeof ClipboardList;
    accent: string;
    iconSurface: string;
  }
> = {
  planning: {
    icon: ClipboardList,
    accent: "text-blue-200",
    iconSurface: "bg-blue-400/15 ring-blue-300/25",
  },
  regulatory: {
    icon: ShieldCheck,
    accent: "text-emerald-200",
    iconSurface: "bg-emerald-400/15 ring-emerald-300/25",
  },
  coordination: {
    icon: UsersRound,
    accent: "text-violet-200",
    iconSurface: "bg-violet-400/15 ring-violet-300/25",
  },
  delivery: {
    icon: PackageCheck,
    accent: "text-amber-200",
    iconSurface: "bg-amber-400/15 ring-amber-300/25",
  },
  growth: {
    icon: TrendingUp,
    accent: "text-orange-200",
    iconSurface: "bg-orange-400/15 ring-orange-300/25",
  },
  communications: {
    icon: MessageSquareText,
    accent: "text-cyan-200",
    iconSurface: "bg-cyan-400/15 ring-cyan-300/25",
  },
};

export default function AgenticOperationsSection() {
  const t = useTranslations("AgenticOperations");

  return (
    <section
      id="agentic-operations"
      aria-labelledby="agentic-operations-title"
      className="relative scroll-mt-28 overflow-hidden bg-[#0D2B45] py-20 text-white sm:py-24"
    >
      <div
        aria-hidden="true"
        className="absolute -top-40 right-[-10rem] h-96 w-96 rounded-full border border-white/10 bg-blue-400/10 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="absolute -bottom-48 left-[-8rem] h-96 w-96 rounded-full bg-orange-400/10 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[1.35fr_0.65fr] lg:items-end">
          <div>
            <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-orange-300/25 bg-orange-300/10 px-4 py-2 text-xs font-bold tracking-[0.2em] text-orange-200 uppercase">
              <Sparkles className="h-4 w-4" aria-hidden />
              {t("eyebrow")}
            </div>
            <h2
              id="agentic-operations-title"
              className="max-w-4xl text-4xl leading-tight font-bold tracking-tight sm:text-5xl lg:text-6xl"
            >
              {t("title")}
            </h2>
            <p className="mt-6 max-w-3xl text-lg leading-8 text-slate-200 sm:text-xl">
              {t("subtitle")}
            </p>
          </div>

          <div className="rounded-3xl border border-white/15 bg-white/[0.07] p-6 shadow-2xl shadow-slate-950/20 backdrop-blur-sm">
            <div className="flex items-start gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-orange-400/15 text-orange-200 ring-1 ring-orange-300/25">
                <ShieldCheck className="h-6 w-6" aria-hidden />
              </div>
              <div>
                <p className="text-sm font-bold tracking-[0.12em] text-orange-200 uppercase">
                  {t("autonomyTitle")}
                </p>
                <p className="mt-2 leading-7 text-slate-200">
                  {t("autonomyBody")}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="relative mt-14">
          <div
            aria-hidden="true"
            className="absolute top-7 right-[8%] left-[8%] hidden border-t border-dashed border-white/20 lg:block"
          />
          <div className="relative grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {AGENTIC_OPERATION_KEYS.map((key, index) => {
              const presentation = areaPresentation[key];
              const Icon = presentation.icon;

              return (
                <motion.article
                  key={key}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ delay: index * 0.06 }}
                  className="group relative flex min-h-[300px] flex-col rounded-3xl border border-white/10 bg-white/[0.06] p-6 shadow-lg shadow-slate-950/10 backdrop-blur-sm transition-colors hover:bg-white/[0.09]"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div
                      className={`flex h-14 w-14 items-center justify-center rounded-2xl ring-1 ${presentation.iconSurface}`}
                    >
                      <Icon className="h-7 w-7" aria-hidden />
                    </div>
                    <span className="font-mono text-sm font-bold tracking-[0.18em] text-white/40">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <h3 className="mt-6 text-2xl font-bold text-white">
                    {t(`areas.${key}.title`)}
                  </h3>
                  <p className="mt-3 leading-7 text-slate-200">
                    {t(`areas.${key}.contribution`)}
                  </p>

                  <div className="mt-auto border-t border-white/10 pt-5">
                    <p
                      className={`text-xs font-bold tracking-[0.16em] uppercase ${presentation.accent}`}
                    >
                      {t("valueLabel")}
                    </p>
                    <p className="mt-2 font-semibold text-white">
                      {t(`areas.${key}.value`)}
                    </p>
                  </div>
                </motion.article>
              );
            })}
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-6 rounded-3xl border border-white/10 bg-slate-950/20 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
          <div className="max-w-3xl">
            <p className="text-lg font-bold text-white">{t("guardrailTitle")}</p>
            <p className="mt-2 leading-7 text-slate-300">{t("guardrailBody")}</p>
          </div>
          <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
            <Button
              size="lg"
              className="bg-[#F37021] text-white hover:bg-[#d95f15]"
              asChild
            >
              <Link href={t("primaryHref")}>
                {t("primaryCta")}
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden />
              </Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white"
              asChild
            >
              <Link href={t("secondaryHref")}>{t("secondaryCta")}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
